import { computed, reactive } from 'vue';
import { useInfiniteQuery } from 'vue-query';
import { UseInfiniteQueryOptions } from 'react-query/types';

import QUERY_KEYS from '@/constants/queryKeys';
import { POOLS } from '@/constants/pools';
import { balancerFarmSubgraphService } from '@/services/balancer/subgraph/balancer-farm-subgraph.service';
import { UserLeaveRequests } from '@/services/balancer/subgraph/types';
import useNetwork from '../useNetwork';
import useWeb3 from '@/services/web3/useWeb3';
import useUserFlakeInfosQuery from './useUserFlakeInfosQuery';

type UserLeaveRequestsResponse = {
  userLeaveRequests: UserLeaveRequests[];
  skip?: number;
};

export default function useUserLeaveRequestsQuery(
  options: UseInfiniteQueryOptions<UserLeaveRequestsResponse> = {}
) {
  // COMPOSABLES
  const { networkId } = useNetwork();
  const { account, isWalletReady } = useWeb3();
  const {
    data: userFlakeInfosQuery,
    isLoading: loadingUserFlakeInfosQuery
  } = useUserFlakeInfosQuery();
  /**
   * COMPUTED
   */
  const enabled = computed(
    () =>
      isWalletReady.value &&
      account.value != null &&
      !loadingUserFlakeInfosQuery
  );
  const pendingFirstIndex = computed(() =>
    userFlakeInfosQuery.value
      ? userFlakeInfosQuery.value[0].pendingFirstIndex
      : ''
  );

  // DATA
  const queryKey = reactive(QUERY_KEYS.FarmPools.UserLeaveRequests(networkId));

  // METHODS
  const queryFn = async ({ pageParam = 0 }) => {
    const userLeaveRequests = await balancerFarmSubgraphService.userLeaveRequests.get(
      {
        first: POOLS.Pagination.PerPage,
        skip: pageParam,
        where: {
          user: account.value,
          index_gte: pendingFirstIndex.value
        }
      }
    );

    return {
      userLeaveRequests,
      skip:
        userLeaveRequests.length >= POOLS.Pagination.PerPage
          ? pageParam + POOLS.Pagination.PerPage
          : undefined
    };
  };

  const queryOptions = reactive({
    enabled,
    getNextPageParam: (lastPage: UserLeaveRequestsResponse) => lastPage.skip,
    ...options
  });

  return useInfiniteQuery<UserLeaveRequestsResponse>(
    queryKey,
    queryFn,
    queryOptions
  );
}
